// Does a scanned parcel identifier belong to the parcel it is being filed on? Pure, unit-tested
// (src/__tests__/acct-match.test.js). Used by DocumentScanner before a notice save and before an Intake-launched
// bill submit.
//
// ⛔ A MISMATCH IS A QUESTION, NOT AN ERROR. Two real cases, opposite answers:
//   • RecNation Jetport (prop-77411): base 27-45-25-20-00000.B010 (the Lee County strap) was replaced by the Lee
//     Property Appraiser folio 10540742 when the 2025 TRIM was filed. Same parcel, a different identifier — the
//     notice was right to be there, and wrong to overwrite the base.
//   • The Pen Factory: one parcel renumbered by LA County, 4268-001-051 (2021) → -052 (2022) → -053 (2023). Each
//     year's bill carries that year's AIN, and the parcel must stay fileable under all three.
// So the caller asks the person, and offers to record the scanned number on that YEAR's row only.
//
// Compared as DIGITS: the same AIN is stored as '4268-001-053', '4268 001 053' and '4268001053'. A prefix match
// covers a collector account that embeds the AIN ('4268 001 052 22 000' is AIN 4268-001-052 + roll year + seq).
const digits = (s) => String(s ?? '').replace(/\D/g, '');

// ⛔ HAWAII ONLY: the bill prints the Tax Map Key wrapped. Maui prints 'RP 2-3-8-001-092-0000-002' for parcel
// 3-8-001-092-0000 — an 'RP ' (real property) prefix, the island digit (2 = Maui) and a lease/CPR unit suffix. The
// leading island digit defeats the prefix rule below, so every Maui bill asked "keep the parcel?" every year. For an HI
// parcel the bill number is ALSO compared with those three stripped — exact digit equality, nothing looser — and
// without the island strip, for a bill that prints none. No other state is affected.
export function hawaiiTmkForms(scanned) {
  const t = String(scanned ?? '').trim().replace(/^RP\s+/i, '').replace(/-\d{3}$/, '');
  const noIsland = t.replace(/^\d-(?=\d)/, '');
  return [...new Set([noIsland, t].map(digits).filter(Boolean))];
}

// scanned: what the document says. known: the parcel's identifiers (base account, that year's account, …).
// opts.state: the parcel's state ('HI' turns on the Tax Map Key forms above).
// True when there is nothing to compare, or when any known identifier matches.
export function acctMatches(scanned, known, opts = {}) {
  const d = digits(scanned);
  const ks = (known || []).map(digits).filter(Boolean);
  if (!d || !ks.length) return true;
  if (ks.some(k => d === k || (k.length >= 6 && d.startsWith(k)) || (d.length >= 6 && k.startsWith(d)))) return true;
  return String(opts.state || '').trim().toUpperCase() === 'HI' && hawaiiTmkForms(scanned).some(f => ks.includes(f));
}
